import { api } from './api';

export type OpsReportPeriod = 'daily' | 'weekly';

export interface Flag {
  sev: 'CRITICAL' | 'HIGH' | 'MEDIUM' | 'LOW';
  area: string;
  msg: string;
}

export interface EcsMetrics {
  desiredCount: number;
  runningCount: number;
  pendingCount: number;
  cpuP50: number;
  cpuP95: number;
  memP50: number;
  memP95: number;
}

export interface ApiGatewayMetrics {
  totalRequests: number;
  error4xxCount: number;
  error5xxCount: number;
  error5xxRate: number;
  latencyP50: number;
  latencyP99: number;
}

export interface CloudfrontMetrics {
  totalRequests: number;
  errorRate: number;
}

export interface AlarmState {
  name: string;
  state: 'OK' | 'ALARM' | 'INSUFFICIENT_DATA';
  reason: string;
}

export interface SecurityMetrics {
  rateLimitViolations: number;
  ipBlocks: number;
  policyViolations: number;
}

export interface N8nMetrics {
  totalExecutions: number;
  failedExecutions: number;
  failureRate: number;
  avgDurationMs: number;
  p95DurationMs: number;
  slowestWorkflows: Array<{ name: string; avgMs: number }>;
  mostFailedWorkflows: Array<{ name: string; failures: number }>;
}

export interface CostMetrics {
  totalUsd: number;
  byProvider: Array<{ provider: string; usd: number; tokens: number }>;
}

export interface WafMetrics {
  allowedRequests: number;
  blockedRequests: number;
  blockRate: number;
}

export interface DynamoDbTableMetrics {
  table: string;
  readThrottles: number;
  writeThrottles: number;
}

export interface N8nWorkflowSummary {
  id: string;
  name: string;
  active: boolean;
  failureRate: number;
  successCount: number;
  failureCount: number;
  lastRun: string | null;
}

export interface OpsMetrics {
  period: OpsReportPeriod;
  generatedAt: string;
  healthScore: number;
  flags: Flag[];
  ecs: EcsMetrics;
  apiGateway: ApiGatewayMetrics;
  cloudfront: CloudfrontMetrics;
  alarms: AlarmState[];
  security: SecurityMetrics;
  n8n: N8nMetrics;
  costs?: CostMetrics;
  waf?: WafMetrics;
  dynamodb?: DynamoDbTableMetrics[];
  n8nWorkflowHealth?: N8nWorkflowSummary[];
}

export async function getOpsMetrics(period: OpsReportPeriod): Promise<OpsMetrics> {
  const response = await api.get('/api/admin/ops-report', { params: { period } });
  return response.data.metrics;
}
